"use client";

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useSupabase } from "@/hooks/useSupabase";
import { toast } from "sonner";
import {
  RFC_CANAIS_SOLICITACAO,
  RFC_CORRECAO_FIELDS_PRIMARY,
  RFC_CORRECAO_FIELDS_SECONDARY,
  RFC_CORRIGIR_RPC,
  RFC_FIELD_LABELS,
  type RfcCorrigirResult,
  type RfcPatchableField,
} from "@/lib/realizadoFinanceiroHistorico";

export interface RealizadoLinhaParaCorrigir {
  id: string;
  conta: string | null;
  tipo: string | null;
  data: string | null;
  numero_documento: string | null;
  nome: string | null;
  debito: number | null;
  credito: number | null;
  saldo: number | null;
  cadastro_federal_cliente: string | null;
  cadastro_federal_fornecedor: string | null;
  memorando: string | null;
  conta_linha_numero: string | null;
  subsidiaria_nome: string | null;
  departamento: string | null;
  area_tratado: string | null;
  classe: string | null;
  sub_area_tratado: string | null;
  valor: number | null;
  tipo_receita_despesa: string | null;
  classificacao_receita_despesa: string | null;
  descricao_receita_despesa: string | null;
  nome_arquivo: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: RealizadoLinhaParaCorrigir | null;
  onSuccess?: () => void;
}

const NUMERIC_FIELDS = new Set<RfcPatchableField>(["debito", "credito", "saldo", "valor"]);

function fieldValue(item: RealizadoLinhaParaCorrigir, key: RfcPatchableField): string {
  const raw = item[key as keyof RealizadoLinhaParaCorrigir];
  if (raw === null || raw === undefined) return "";
  if (key === "data" && typeof raw === "string") return raw.slice(0, 10);
  return String(raw);
}

function CorrigirField({
  fieldKey,
  draft,
  setDraft,
}: {
  fieldKey: RfcPatchableField;
  draft: Partial<Record<RfcPatchableField, string>>;
  setDraft: React.Dispatch<React.SetStateAction<Partial<Record<RfcPatchableField, string>>>>;
}) {
  const isDate = fieldKey === "data";
  const isNumeric = NUMERIC_FIELDS.has(fieldKey);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`rfc-${fieldKey}`} className="text-xs">
        {RFC_FIELD_LABELS[fieldKey]}
      </Label>
      <Input
        id={`rfc-${fieldKey}`}
        type={isDate ? "date" : "text"}
        inputMode={isNumeric ? "decimal" : undefined}
        value={draft[fieldKey] ?? ""}
        onChange={(e) => setDraft((d) => ({ ...d, [fieldKey]: e.target.value }))}
        className="h-9"
      />
    </div>
  );
}

export function RealizadoCorrigirLinhaDialog({ open, onOpenChange, item, onSuccess }: Props) {
  const { supabase } = useSupabase();
  const [solicitadoPor, setSolicitadoPor] = useState("");
  const [canal, setCanal] = useState<string>("email");
  const [referencia, setReferencia] = useState("");
  const [solicitadoEm, setSolicitadoEm] = useState("");
  const [motivo, setMotivo] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [draft, setDraft] = useState<Partial<Record<RfcPatchableField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const initial: Partial<Record<RfcPatchableField, string>> = {};
    const all = [...RFC_CORRECAO_FIELDS_PRIMARY, ...RFC_CORRECAO_FIELDS_SECONDARY];
    for (const k of all) {
      initial[k] = fieldValue(item, k);
    }
    setDraft(initial);
    setSolicitadoPor("");
    setCanal("email");
    setReferencia("");
    setSolicitadoEm("");
    setMotivo("");
    setObservacoes("");
    setShowMore(false);
  }, [open, item]);

  const patch = useMemo(() => {
    if (!item) return {};
    const out: Record<string, string | number> = {};
    const keys = [...RFC_CORRECAO_FIELDS_PRIMARY, ...(showMore ? RFC_CORRECAO_FIELDS_SECONDARY : [])];
    for (const k of keys) {
      const novo = draft[k] ?? "";
      const antigo = fieldValue(item, k);
      if (novo === antigo) continue;
      if (NUMERIC_FIELDS.has(k)) {
        const n = novo.trim() === "" ? null : Number(novo.replace(/\./g, "").replace(",", "."));
        if (novo.trim() !== "" && Number.isNaN(n)) continue;
        out[k] = n as number;
      } else {
        out[k] = novo;
      }
    }
    return out;
  }, [item, draft, showMore]);

  const hasChanges = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!item) return;
    if (!solicitadoPor.trim()) {
      toast.error("Informe quem solicitou o ajuste (ex.: nome do financeiro).");
      return;
    }
    if (!canal) {
      toast.error("Selecione por onde veio a solicitação.");
      return;
    }
    if (!observacoes.trim() && !motivo.trim()) {
      toast.error("Informe as observações (pode colar o trecho do e-mail) ou um resumo do motivo.");
      return;
    }
    if (!hasChanges) {
      toast.error("Altere ao menos um campo da linha antes de salvar.");
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.rpc(RFC_CORRIGIR_RPC, {
        p_id: item.id,
        p_patch: patch,
        p_motivo: motivo.trim() || observacoes.trim().slice(0, 200),
        p_solicitado_por: solicitadoPor.trim(),
        p_canal: canal,
        p_referencia_solicitacao: referencia.trim() || null,
        p_observacoes: observacoes.trim() || motivo.trim(),
        p_solicitado_em: solicitadoEm ? new Date(solicitadoEm).toISOString() : null,
      });
      if (error) throw error;
      const result = data as RfcCorrigirResult;
      if (!result?.success) {
        throw new Error(result?.error || "Falha ao corrigir registro");
      }
      toast.success("Correção registrada com rastreio completo no histórico.");
      onOpenChange(false);
      onSuccess?.();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erro ao salvar correção";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Corrigir linha do realizado</DialogTitle>
          <DialogDescription>
            Registre quem pediu, por qual canal e o contexto (ex.: e-mail do financeiro). Isso fica salvo para auditoria futura,
            junto com quem executou a alteração no sistema e quando.
          </DialogDescription>
        </DialogHeader>

        {item ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <p className="text-sm font-medium">Rastreio da solicitação</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="rfc-solicitado-por">
                    Solicitado por <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="rfc-solicitado-por"
                    placeholder="Ex.: Ana Silva — Financeiro"
                    value={solicitadoPor}
                    onChange={(e) => setSolicitadoPor(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rfc-canal">
                    Por onde <span className="text-destructive">*</span>
                  </Label>
                  <Select value={canal} onValueChange={setCanal}>
                    <SelectTrigger id="rfc-canal">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RFC_CANAIS_SOLICITACAO.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rfc-solicitado-em">Data da solicitação</Label>
                  <Input
                    id="rfc-solicitado-em"
                    type="datetime-local"
                    value={solicitadoEm}
                    onChange={(e) => setSolicitadoEm(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="rfc-referencia">Referência (assunto do e-mail, protocolo…)</Label>
                  <Input
                    id="rfc-referencia"
                    placeholder="Ex.: E-mail 12/05/2026 — Ajustes realizado RH"
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="rfc-motivo">Resumo curto</Label>
                  <Input
                    id="rfc-motivo"
                    placeholder="Ex.: Corrigir área/subárea da linha X"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="rfc-observacoes">
                    Observações / texto do pedido <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    id="rfc-observacoes"
                    placeholder="Cole aqui o trecho relevante do e-mail ou descreva o pedido com detalhes para auditoria."
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Ao salvar, o sistema registra também <strong>quando</strong> a alteração foi aplicada e{" "}
                <strong>qual usuário logado</strong> executou (campo técnico no histórico).
              </p>
            </div>

            <Separator />

            <p className="text-xs text-muted-foreground font-mono break-all">Linha: {item.id}</p>

            <div className="grid gap-3 sm:grid-cols-2">
              {RFC_CORRECAO_FIELDS_PRIMARY.map((key) => (
                <CorrigirField key={key} fieldKey={key} draft={draft} setDraft={setDraft} />
              ))}
            </div>

            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setShowMore(!showMore)}>
              {showMore ? "Ocultar campos adicionais" : "Mais campos (conta, nome, memorando…)"}
            </Button>

            {showMore ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {RFC_CORRECAO_FIELDS_SECONDARY.map((key) => (
                  <CorrigirField key={key} fieldKey={key} draft={draft} setDraft={setDraft} />
                ))}
              </div>
            ) : null}

            {hasChanges ? (
              <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
                <p className="font-medium text-foreground">Campos da linha que serão alterados:</p>
                <ul className="list-disc pl-4 text-muted-foreground">
                  {Object.keys(patch).map((k) => (
                    <li key={k}>{RFC_FIELD_LABELS[k as RfcPatchableField] ?? k}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving || !item}>
            {saving ? "Salvando…" : "Salvar correção"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
